import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { CardContent } from '../ui/card';
import { Save, MessageSquare, Loader2, ExternalLink, Send } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import MobileInput from '../ui/mobile-input';
import { validateMobile } from '../../utils/phoneValidation';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

export default function PlatformSmsSettings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [form, setForm] = useState({
    enabled: false,
    provider: 'twilio',
    account_sid: '',
    auth_token: '',
    auth_token_masked: '',
    from: '',
    otp_expiry_minutes: 10,
    otp_max_attempts: 5,
    otp_resend_cooldown: 60,
    is_configured: false,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('platform/sms-settings')
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data || {};
        setForm({
          enabled: !!d.enabled,
          provider: d.provider || 'twilio',
          account_sid: d.account_sid || '',
          auth_token: '',
          auth_token_masked: d.auth_token_masked || '',
          from: d.from || '',
          otp_expiry_minutes: d.otp_expiry_minutes ?? 10,
          otp_max_attempts: d.otp_max_attempts ?? 5,
          otp_resend_cooldown: d.otp_resend_cooldown ?? 60,
          is_configured: !!d.is_configured,
        });
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to load SMS settings',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        provider: form.provider,
        account_sid: form.account_sid.trim(),
        auth_token: form.auth_token.trim() || form.auth_token_masked,
        from: form.from.trim(),
        otp_expiry_minutes: Number(form.otp_expiry_minutes) || 10,
        otp_max_attempts: Number(form.otp_max_attempts) || 5,
        otp_resend_cooldown: Number(form.otp_resend_cooldown) || 60,
      };
      const res = await api.post('platform/sms-settings', payload);
      const d = res.data?.data || {};
      setForm((prev) => ({
        ...prev,
        enabled: !!d.enabled,
        provider: d.provider || 'twilio',
        account_sid: d.account_sid || prev.account_sid,
        auth_token: '',
        auth_token_masked: d.auth_token_masked || '',
        from: d.from || prev.from,
        otp_expiry_minutes: d.otp_expiry_minutes ?? prev.otp_expiry_minutes,
        otp_max_attempts: d.otp_max_attempts ?? prev.otp_max_attempts,
        otp_resend_cooldown: d.otp_resend_cooldown ?? prev.otp_resend_cooldown,
        is_configured: !!d.is_configured,
      }));
      toast({ title: 'Saved', description: 'SMS gateway settings updated' });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to save SMS settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    const phoneErr = validateMobile(testPhone, { required: true });
    if (phoneErr) {
      toast({ title: 'Error', description: phoneErr, variant: 'destructive' });
      return;
    }
    setTesting(true);
    try {
      await api.post('platform/sms-settings/test', { phone: testPhone });
      toast({ title: 'Sent', description: 'Test SMS sent successfully' });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to send test SMS',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <CardContent className="p-6 space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 p-4 text-sm">
        <p className="font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" /> Twilio SMS
        </p>
        <p className="text-muted-foreground mt-1">
          Used for mobile number OTP verification on web and mobile apps. Get credentials from the
          {' '}
          <a
            href="https://console.twilio.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            Twilio Console <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label>Enable SMS gateway</Label>
          <p className="text-xs text-muted-foreground mt-1">
            When disabled, OTP codes are written to server logs (development fallback).
          </p>
        </div>
        <Switch
          checked={form.enabled}
          onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
        />
      </div>

      <div>
        <Label>Provider</Label>
        <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
          <SelectTrigger className="mt-1.5 rounded-lg max-w-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="twilio">Twilio</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Account SID</Label>
        <Input
          value={form.account_sid}
          onChange={(e) => setForm({ ...form, account_sid: e.target.value })}
          className="mt-1.5 rounded-lg font-mono text-sm"
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          autoComplete="off"
        />
      </div>

      <div>
        <Label>Auth Token</Label>
        <Input
          type="password"
          value={form.auth_token}
          onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
          className="mt-1.5 rounded-lg font-mono text-sm"
          placeholder={form.auth_token_masked || 'Enter auth token'}
          autoComplete="new-password"
        />
        {form.auth_token_masked && !form.auth_token && (
          <p className="text-xs text-muted-foreground mt-1">
            Saved: {form.auth_token_masked}. Leave blank to keep unchanged.
          </p>
        )}
      </div>

      <div>
        <Label>From number</Label>
        <Input
          value={form.from}
          onChange={(e) => setForm({ ...form, from: e.target.value })}
          className="mt-1.5 rounded-lg font-mono text-sm"
          placeholder="+1234567890"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Twilio phone number or messaging service sender in E.164 format.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label>OTP expiry (minutes)</Label>
          <Input
            type="number"
            min={5}
            max={10}
            value={form.otp_expiry_minutes}
            onChange={(e) => setForm({ ...form, otp_expiry_minutes: e.target.value })}
            className="mt-1.5 rounded-lg"
          />
        </div>
        <div>
          <Label>Max attempts</Label>
          <Input
            type="number"
            min={1}
            max={20}
            value={form.otp_max_attempts}
            onChange={(e) => setForm({ ...form, otp_max_attempts: e.target.value })}
            className="mt-1.5 rounded-lg"
          />
        </div>
        <div>
          <Label>Resend cooldown (sec)</Label>
          <Input
            type="number"
            min={1}
            max={600}
            value={form.otp_resend_cooldown}
            onChange={(e) => setForm({ ...form, otp_resend_cooldown: e.target.value })}
            className="mt-1.5 rounded-lg"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} className={PRIMARY_BTN} disabled={saving}>
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save SMS settings'}
        </Button>
        {form.is_configured && (
          <span className="text-sm text-green-600">Configured</span>
        )}
      </div>

      <div className="rounded-lg border p-4 space-y-3">
        <div>
          <Label>Send test SMS</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Uses the saved Twilio credentials. Save settings first if you just changed them.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Mobile number</Label>
            <MobileInput value={testPhone} onChange={setTestPhone} disabled={testing} />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleTest}
            disabled={testing || !form.is_configured}
            className="rounded-lg"
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? 'Sending…' : 'Send test'}
          </Button>
        </div>
      </div>
    </CardContent>
  );
}
